import {HttpClient, HttpErrorResponse} from '@angular/common/http';

import {Observable,  timer } from 'rxjs';
import { BaseEntity, BnDateUtils, SentidoOrdenacao, EntityPagedResult } from '@bndes/bndes-angular-utils';
import { mergeMap, retryWhen, map } from 'rxjs/operators';
import { blockUi } from '@smdapp/ng-shared/components/loading-overlay/loading-interceptor';

export const progressEventErrorRetryStrategy = ({  maxRetryAttempts = 3,scalingDuration = 500}: {maxRetryAttempts?: number,scalingDuration?: number} = {}) => (attempts: Observable<any>) => 
{
  return attempts.pipe(
    mergeMap((error, i) => 
    {
      const retryAttempt = i + 1;
      const ehErroProgressEvent = (error instanceof HttpErrorResponse) && error.error instanceof ProgressEvent;
      if (retryAttempt > maxRetryAttempts || !ehErroProgressEvent) 
      {
        throw(error);
      }
      return timer(retryAttempt * scalingDuration);
    })
  );
};

export interface IBaseEntityService<T extends BaseEntity> {
  getHttpClient(): HttpClient;
}

export abstract class BaseEntityService<T extends BaseEntity> implements IBaseEntityService<T> {

  static BASE_REST = './rest';

  constructor(protected http: HttpClient) {
  }

  public abstract getNewEntity(): T;

  protected abstract getResourceURL(): string;

  protected convertToType(value): T {
    return <T>this.getNewEntity().extend(value);
  }

  public getHttpClient(): HttpClient {
    return this.http;
  }


  create(options?: {}, pathParams?:{}): Observable<T> {
    return this.http.get(`${this.getBaseURL(pathParams)}/create`, blockUi(options)).pipe(map(response => this.convertToType(response)));
  }

  findById(id: number, options?: {}, pathParams?:{}): Observable<T> {

    return this.http.
          get(`${this.getBaseURL(pathParams)}/${id}`, blockUi(options))
          .pipe(retryWhen(progressEventErrorRetryStrategy()), map(response => this.convertToType(response)));
  }

  private doHttpGetWithParams(params?: any, options: any = {}, pathParams?:{}): Observable<any> {
    options = {...options, params};
    BnDateUtils.convertDatesToString(options.params);
    return this.http.get(`${this.getBaseURL(pathParams)}`, blockUi(options)).pipe(retryWhen(progressEventErrorRetryStrategy()));  
  }

  find(params?: any, options?: any, pathParams?:{}): Observable<T[]> {
    return this.doHttpGetWithParams(params, options, pathParams).pipe(
      retryWhen(progressEventErrorRetryStrategy()), 
      map((response: T[]) => response.map(item => this.convertToType(item))));
  }

  findAll(options?: any, pathParams?:{}): Observable<T[]> {
    return this.find(null, options, pathParams);
  }

  findOrderedAndPaged(numeroPagina: number = 1,
            tamanhoPagina: number = 20,
            parametroOrdenacao?: string,
            sentidoOrdenacao?: SentidoOrdenacao,
            params?: any,
            options?: any, pathParams?:{}
          ): Observable<EntityPagedResult<T>> {

    let orderBy: string;

    if (parametroOrdenacao) {
      const sentido = sentidoOrdenacao ? sentidoOrdenacao : SentidoOrdenacao.ASCENDENTE;
      orderBy = `${parametroOrdenacao}+${sentido}`;
    }

    let parametrosRequisicao = {};

    if (orderBy) {
      parametrosRequisicao = Object.assign(parametrosRequisicao, { orderBy: orderBy });
    }

    if (params) {
      parametrosRequisicao = Object.assign(parametrosRequisicao, params);
    }

    return this.findPaged(numeroPagina, tamanhoPagina, parametrosRequisicao, options, pathParams);
  }


  findPaged(numeroPagina: number = 1,
            tamanhoPagina: number = 20,
            params?: any,
            options?: any, pathParams?:{}
          ): Observable<EntityPagedResult<T>> {


    const pagedParams = {
      ...params,
      numeroPagina: numeroPagina,
      tamanhoPagina: tamanhoPagina,
    };

    return this.doHttpGetWithParams(pagedParams, options, pathParams).pipe(
      retryWhen(progressEventErrorRetryStrategy()),  
      map(
        (response: EntityPagedResult<T>) => {
          const pagedResult = Object.assign(new EntityPagedResult<T>(), response);

          if (pagedResult.list) {
            pagedResult.list = pagedResult.list.map(item => this.convertToType(item));
          }

          return pagedResult;
        }
      ));
  }

  save(entity: T, options?: any, pathParams?:{}): Observable<T> {
    if (entity.id) {
      return this.update(entity, blockUi(options), pathParams).pipe(map(
        response => this.convertToType(response)
      ));
    }

    return this.http.post(`${this.getBaseURL(pathParams)}`, entity, blockUi(options)).pipe(map(response => this.convertToType(response)));
  }

  update(entity: T, options?: any, pathParams?:{}): Observable<T> {
    return this.http.post(`${this.getBaseURL(pathParams)}/${entity.id}`, entity, blockUi(options)).pipe(map(response => this.convertToType(response)));
  }

  remove(id: number, options?: any, pathParams?:{}) {
    return this.http.delete(`${this.getBaseURL(pathParams)}/${id}`, blockUi(options));
  }

  getBaseURL(pathParams): string
  {
    let resourceUrl: string = this.getResourceURL();
    if (pathParams)
    {
        for (let property in pathParams)
        {
            if (pathParams.hasOwnProperty(property))
            {
                resourceUrl = resourceUrl.replace(`[${property}]`, pathParams[property]);
            }
        }
    }
    return `${BaseEntityService.BASE_REST}/${resourceUrl}`;
  }

}
